'use client';

import { useEffect } from 'react';
import { restaurantsClient } from '@bip/api-client';

interface RestaurantsCitySyncProps {
  /** City name as resolved by the listing page (from API or slug fallback). */
  cityName: string;
}

/**
 * Today + tomorrow as `YYYY-MM-DD` strings. Mirrors the hotels-domain
 * SearchBar's date format so the `searches[]` entry we seed below is
 * indistinguishable from one the hotels SearchBar would have written
 * on its own submit.
 */
function getCurrentAndNextDay(): { startDate: string; endDate: string } {
  const today = new Date();
  const fmt = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const end = new Date(start);
  end.setDate(start.getDate() + 1);
  return { startDate: fmt(start), endDate: fmt(end) };
}

/**
 * Seeds the two cross-domain localStorage entries (sports
 * `theatre-searches-city` and hotels `searches[]`) from the SearchLocation
 * CMS doc for the current restaurant listing page's city.
 *
 * Identical contract to the sports `CityRecentSearchSync` — the
 * restaurants-service now hosts the same `getCityDestId` endpoint
 * (queries the shared CMS-owned `SearchLocation` collection), so
 * this component stays inside the restaurants client and doesn't
 * cross into the sports client just to seed cross-domain blobs.
 *
 * Runs on the destination page (after navigation is complete) so the
 * request never races a `router.push`. No-ops on subsequent renders
 * for the same city via the `cityName`-keyed dep array, and silently
 * swallows failures (backend down, no matching CMS row, localStorage
 * disabled) since cross-domain recent-search seeding is a convenience,
 * not a hard requirement of the restaurants flow.
 */
export function RestaurantsCitySync({ cityName }: RestaurantsCitySyncProps) {
  useEffect(() => {
    if (!cityName) return;
    let cancelled = false;
    const cityNameQuery = cityName.split(',')[0].trim();
    if (!cityNameQuery) return;

    restaurantsClient
      .getCityDestId(cityNameQuery)
      .then((res) => {
        if (cancelled) return;
        if (res?.message !== 'success' || !res?.data) {
          // Backend explicitly told us no row was found, or there
          // was a server-side error. Surface the reason in the
          // console so ops can spot CMS gaps; the listing page
          // itself still renders fine without the localStorage
          // seed — it's a cross-domain convenience, not a hard
          // dependency of the restaurants flow.
          // eslint-disable-next-line no-console
          console.warn(
            '[RestaurantsCitySync] skipping localStorage seed:',
            res?.message ?? 'no response',
            res?.error ?? '',
          );
          return;
        }
        const doc = res.data;
        try {
          // `theatre-searches-city` is INTENTIONALLY not written
          // here. That blob is the source of truth for the hero's
          // priority-1 auto-resolve on the next visit, and per spec
          // it should only be updated when the user explicitly
          // searches a city + clicks "Find Table" — see the
          // `handleSearch` submit handler in `RestaurantsHero.tsx`.
          // Firing it on every mount of the listing page (which is
          // what this component does — including URL-driven /
          // bookmarked navigations) would auto-restore a city the
          // user didn't actually choose this session.

          // Hotels-domain `searches[]` array — same shape the hotels
          // SearchBar writes itself, capped at 3 entries, dedup
          // against the first existing entry. Skipped when the
          // SearchLocation row has no `_id` (defensive — shouldn't
          // happen for a real CMS row).
          const destId = doc._id ? String(doc._id) : '';
          if (!destId) return;
          const { startDate, endDate } = getCurrentAndNextDay();
          const newSearch = {
            dest_id: destId,
            checkin: startDate,
            checkout: endDate,
            rooms: 1,
            adults: '2',
            children: '',
            ratecode: '',
            // Label the entry so the hotels home "recent searches" section
            // shows the city name (it reads `_label`) — mirrors the sports
            // `CityRecentSearchSync` convention.
            _label: [doc.city, doc.state, doc.country].filter(Boolean).join(', '),
          };
          const previousSearches = JSON.parse(
            localStorage.getItem('searches') || '[]',
          );
          // Dedup by `dest_id` across the WHOLE list (not just index 0) —
          // a stale entry for this destination sitting further down the
          // array must be dropped and the fresh one always unshifted to
          // the front, so a search never leaves the city split across
          // two positions or stuck below index 0.
          const updated = (
            Array.isArray(previousSearches) ? previousSearches : []
          ).filter(
            (r: { dest_id?: string }) => r?.dest_id !== newSearch.dest_id,
          );
          updated.unshift(newSearch);
          if (updated.length > 3) updated.pop();
          localStorage.setItem('searches', JSON.stringify(updated));
        } catch {
          // localStorage disabled / quota / parse error — leave
          // existing entries untouched.
        }
      })
      .catch(() => {
        // Backend unreachable / 404 / non-JSON response — skip the
        // writes silently. Listing page renders regardless.
      });

    return () => {
      cancelled = true;
    };
  }, [cityName]);

  return null;
}
